import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Scan, Package, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * Slice D5 — Per-section SFG Put-Away clone (Salem-untouched).
 *
 * Literal-copy of pages/WMS/SFGPutAway.jsx with the 2 backend URLs swapped
 * to per-section endpoints. Salem's SFGPutAway.jsx is NOT imported nor
 * modified.
 */

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};

const PerSectionSfgPutAwayClone = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug] || slug;
  const backRoute = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/sfg-put-away`;

  const bundleInputRef = useRef(null);
  const staffInputRef = useRef(null);
  const binInputRef = useRef(null);

  const [staffId, setStaffId] = useState('');
  const [staffValidated, setStaffValidated] = useState(false);
  const [staffName, setStaffName] = useState('');

  const [bundleCode, setBundleCode] = useState('');
  const [bundleDetails, setBundleDetails] = useState(null);
  const [suggestedBin, setSuggestedBin] = useState(null);

  const [binLocation, setBinLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [putAwaySuccess, setPutAwaySuccess] = useState(false);

  useEffect(() => {
    staffInputRef.current?.focus();
  }, []);

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter Staff ID');
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffValidated(true);
        setStaffName(response.data.staff_name);
        toast.success(`Staff validated: ${response.data.staff_name}`);
        setTimeout(() => bundleInputRef.current?.focus(), 100);
      } else {
        toast.error('Invalid Staff ID');
      }
    } catch (error) {
      toast.error('Staff validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBundleScan = async () => {
    if (!bundleCode.trim()) {
      toast.error('Please scan or enter bundle code');
      return;
    }
    try {
      setLoading(true);
      setPutAwaySuccess(false);
      const response = await apiClient.post(
        `${apiBase}/validate-bundle/${bundleCode}?staff_id=${encodeURIComponent(staffId)}`
      );
      setBundleDetails(response.data.bundle);
      setSuggestedBin(response.data.suggested_bin);
      setBinLocation('');
      if (response.data.suggested_bin) {
        toast.success(`Bundle validated. Suggested bin: ${response.data.suggested_bin.bin_code}`);
      } else {
        toast.warning('Bundle validated but no bin suggestion available');
      }
      setTimeout(() => binInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bundle validation failed');
      setBundleDetails(null);
      setSuggestedBin(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePutAway = async () => {
    if (!bundleDetails) {
      toast.error('Please scan a bundle first');
      return;
    }
    if (!binLocation.trim()) {
      toast.error('Please enter bin location');
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.post(apiBase, {
        staff_id: staffId,
        bundle_code: bundleCode,
        bin_location: binLocation,
      });
      toast.success(response.data.message);
      setPutAwaySuccess(true);
      setTimeout(() => {
        setBundleCode('');
        setBundleDetails(null);
        setSuggestedBin(null);
        setBinLocation('');
        setPutAwaySuccess(false);
        bundleInputRef.current?.focus();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Put-away failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') action();
  };

  const resetAll = () => {
    setStaffId('');
    setStaffValidated(false);
    setStaffName('');
    setBundleCode('');
    setBundleDetails(null);
    setSuggestedBin(null);
    setBinLocation('');
    setPutAwaySuccess(false);
    staffInputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`per-section-sfg-put-away-${slug}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate(backRoute)}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">SFG Put-Away</h1>
            <p className="text-sm text-[#E8DCC8]">{sectionName}</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">Staff Validation</h2>
          {!staffValidated ? (
            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Staff ID</Label>
                <Input
                  ref={staffInputRef}
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                  placeholder="Scan or enter Staff ID"
                  className="mt-1"
                  data-testid="staff-id-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleStaffValidate}
                  disabled={loading}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                  data-testid="validate-staff-btn"
                >
                  Validate
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">{staffName}</span>
                <span className="text-sm text-green-600">({staffId})</span>
              </div>
              <Button variant="ghost" size="sm" onClick={resetAll} data-testid="change-staff-btn">
                Change
              </Button>
            </div>
          )}
        </div>

        {staffValidated && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">
              <Scan className="w-5 h-5 inline mr-2" />
              Scan Bundle
            </h2>
            <div className="flex gap-3 mb-4">
              <div className="flex-1">
                <Label>Bundle Barcode</Label>
                <Input
                  ref={bundleInputRef}
                  value={bundleCode}
                  onChange={(e) => setBundleCode(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleBundleScan)}
                  placeholder="Scan bundle barcode"
                  className="mt-1"
                  data-testid="bundle-code-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleBundleScan}
                  disabled={loading || !bundleCode}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                  data-testid="validate-bundle-btn"
                >
                  Validate
                </Button>
              </div>
            </div>

            {bundleDetails && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-[#1A1F2E] mb-2">Bundle Details</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#5A5F6E]">Bundle Code:</span>
                    <span className="ml-2 font-medium">{bundleDetails.bundle_code}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">SKU:</span>
                    <span className="ml-2 font-medium">{bundleDetails.sku_code || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">USN Count:</span>
                    <span className="ml-2 font-medium">{bundleDetails.usn_count}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Velocity Class:</span>
                    <span
                      className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${
                        bundleDetails.velocity_class === 'A'
                          ? 'bg-green-100 text-green-700'
                          : bundleDetails.velocity_class === 'B'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {bundleDetails.velocity_class || 'C'}
                    </span>
                  </div>
                  {bundleDetails.design_name && (
                    <div className="col-span-2">
                      <span className="text-[#5A5F6E]">Design:</span>
                      <span className="ml-2 font-medium">{bundleDetails.design_name}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {bundleDetails && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">
              <MapPin className="w-5 h-5 inline mr-2" />
              Bin Location
            </h2>
            {suggestedBin && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800">
                      Suggested Bin: <strong>{suggestedBin.bin_code}</strong>
                    </span>
                  </div>
                  {suggestedBin.capacity > 0 && (
                    <span
                      className={`text-sm font-medium px-2 py-1 rounded ${
                        suggestedBin.available_slots === 0
                          ? 'bg-red-100 text-red-700'
                          : suggestedBin.available_slots <= 2
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {suggestedBin.current_stock}/{suggestedBin.capacity} bundles ({suggestedBin.available_slots} available)
                    </span>
                  )}
                </div>
                {suggestedBin.suggestion_reason && (
                  <p className="text-xs text-green-700 italic">
                    Reason: {suggestedBin.suggestion_reason}
                  </p>
                )}
              </div>
            )}
            {!suggestedBin && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <span className="text-yellow-800">
                    No bin suggestion available. Please enter bin location manually.
                  </span>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Confirm/Override Bin Location</Label>
                <Input
                  ref={binInputRef}
                  value={binLocation}
                  onChange={(e) => setBinLocation(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handlePutAway)}
                  placeholder="Scan or enter bin location"
                  className="mt-1"
                  data-testid="bin-location-input"
                />
              </div>
            </div>
          </div>
        )}

        {bundleDetails && binLocation && (
          <Button
            onClick={handlePutAway}
            disabled={loading || putAwaySuccess}
            className={`w-full h-14 text-lg ${
              putAwaySuccess ? 'bg-green-500 hover:bg-green-500' : 'bg-[#2D5F4F] hover:bg-[#234A3D]'
            } text-white`}
            data-testid="putaway-btn"
          >
            {putAwaySuccess ? (
              <>
                <CheckCircle className="w-6 h-6 mr-2" />
                Put Away Successful!
              </>
            ) : (
              <>
                <Package className="w-6 h-6 mr-2" />
                Confirm Put-Away
              </>
            )}
          </Button>
        )}
      </main>
    </div>
  );
};

export default PerSectionSfgPutAwayClone;
